import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { FileText, Upload, ArrowLeft, CheckCircle, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudentAssignments = () => {
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [textContent, setTextContent] = useState('');
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/student/login');
    } else if (user && user.role !== 'student') {
      navigate('/');
    } else if (user) {
      fetchAssignments();
    }
  }, [user, authLoading, navigate]);

  const fetchAssignments = async () => {
    try {
      const res = await axios.get(`${API}/student/assignments`, { withCredentials: true });
      setAssignments(res.data);
    } catch (error) {
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!textContent && !file) {
      toast.error('Please provide text content or upload a file');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      if (textContent) formData.append('text_content', textContent);
      if (file) formData.append('file', file);

      await axios.post(`${API}/student/assignment/${lessonId}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Assignment submitted successfully!');
      navigate('/student/assignments');
    } catch (error) {
      toast.error('Failed to submit assignment');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AEEF]"></div>
      </div>
    );
  }

  // Assignment Submission View
  if (lessonId) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-8">
          <div className="flex items-center gap-4 mb-8">
            <Link to="/student/courses" className="text-[#94A3B8] hover:text-white">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-white">Submit Assignment</h1>
              <p className="text-[#94A3B8]">Upload your work</p>
            </div>
          </div>

          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#94A3B8] mb-2">
                  Text Submission
                </label>
                <textarea
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  placeholder="Describe your work, include links to your project, or write your solution..."
                  className="bg-[#060F22] border border-[#00AEEF]/30 text-white placeholder-[#94A3B8]/50 focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] rounded-xl px-4 py-3 w-full min-h-[200px] transition-all resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#94A3B8] mb-2">
                  File Upload (Optional)
                </label>
                <div className="border-2 border-dashed border-[#00AEEF]/30 rounded-xl p-8 text-center hover:border-[#00AEEF] transition-colors">
                  <input
                    type="file"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                    {file ? (
                      <p className="text-[#00FFE0]">{file.name}</p>
                    ) : (
                      <p className="text-[#94A3B8]">Click to upload or drag and drop</p>
                    )}
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#FF6A00] text-white font-bold rounded-xl px-6 py-4 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit Assignment'}
                <CheckCircle className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Assignments List View
  return (
    <div className="pt-20 min-h-screen bg-[#060F22]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/student/dashboard" className="text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-white">My Assignments</h1>
            <p className="text-[#94A3B8]">View your submitted work</p>
          </div>
        </div>

        {assignments.length > 0 ? (
          <div className="space-y-4">
            {assignments.map((assignment) => (
              <div
                key={assignment.id}
                className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#FF6A00]/20 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-[#FF6A00]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">Assignment Submission</h3>
                      <p className="text-[#94A3B8] text-sm mb-2">
                        Submitted: {new Date(assignment.submitted_at).toLocaleDateString()}
                      </p>
                      {assignment.text_content && (
                        <p className="text-[#94A3B8] text-sm line-clamp-2">{assignment.text_content}</p>
                      )}
                      {assignment.file && (
                        <p className="text-[#00AEEF] text-sm mt-2">
                          📎 {assignment.file.filename}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    assignment.status === 'submitted' ? 'bg-[#00AEEF]/20 text-[#00AEEF]' :
                    assignment.status === 'graded' ? 'bg-[#00FFE0]/20 text-[#00FFE0]' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {assignment.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-12 text-center">
            <FileText className="w-16 h-16 text-[#94A3B8] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">No Assignments Yet</h2>
            <p className="text-[#94A3B8] mb-6">Complete course lessons to unlock assignments</p>
            <Link
              to="/student/courses"
              className="inline-flex items-center gap-2 bg-[#00AEEF] text-white font-bold rounded-xl px-6 py-3"
            >
              Go to Courses
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentAssignments;
